// ReportTable — unified table for report pages.
//
// Replaces ad-hoc MUI <DataGrid> usage. Built on a CSS grid (no <table>),
// styled with design tokens, optimised for sparse-numeric report data.
//
// columns[]: Array<{
//   field         : string  — row key
//   header        : string  — column header (uppercase 10px label)
//   width?        : string|number — CSS grid track ('120px' | '1.4fr' | 130 | 'minmax(...)')
//   align?        : 'left' | 'right' | 'center'
//   numeric?      : boolean — monospace + right-align (sets type='number' for sort)
//   sales?        : boolean — same as numeric, but: 0 → muted; >0 → green
//   phone?        : boolean — formats as <a href="tel:">
//   email?        : boolean — formats as <a href="mailto:">
//   percent?      : { thresholds?: [warn, good] } — renders as Badge with semantic color
//   sortable?     : boolean (default true for known types)
//   render?       : (value, row) => ReactNode — full custom renderer
//   format?       : (value) => string — custom string formatter (still uses default cell)
//   sticky?       : boolean — first/last column sticks while horizontally scrolling
//   minWidth?     : number — when columns total exceeds container, table scrolls
// }>
//
// Props:
//   rows          : Array<row>
//   columns       : columns[]
//   loading       : boolean
//   error         : string | null
//   emptyText     : string ('Brak danych')
//   pageSize      : number  (default 25; 0 = no pagination)
//   pageSizeOptions : number[] (default [10, 25, 50, 100])
//   initialSort   : { field, dir: 'asc' | 'desc' }
//   totals        : boolean — render footer with sum() for numeric/sales columns
//   onRowClick    : (row) => void
//   density       : 'comfortable' | 'compact'
//   getRowId      : (row, idx) => any (default looks for `id` then row[firstField])
import React, { useState, useMemo } from 'react'
import { theme as defaultTheme, T, fmt } from './tokens'
import { Icon } from './icons'

export default function ReportTable({
  rows = [],
  columns = [],
  loading = false,
  error = null,
  emptyText = 'Brak danych dla wybranych filtrów.',
  pageSize: initialPageSize = 25,
  pageSizeOptions = [10, 25, 50, 100],
  initialSort = null,
  totals = false,
  onRowClick,
  density = 'comfortable',
  theme = defaultTheme,
  getRowId,
  // ─ Server-side pagination (optional) ───────────────────────
  // When set, ReportTable does NOT paginate or sort the rows itself —
  // it just renders what's passed in `rows` and emits change events.
  serverPagination = false,
  rowCount = null,             // server-known total
  page: pageProp = null,       // controlled current page
  onPageChange,                // (newPage) => void
  onPageSizeChange,            // (newSize) => void
}) {
  const cols = useMemo(() => columns.map(normalizeColumn), [columns])
  const [sort, setSort] = useState(initialSort)
  const [pageSize, setPageSize] = useState(initialPageSize)
  const [internalPage, setInternalPage] = useState(0)
  const page = serverPagination ? (pageProp ?? 0) : internalPage
  const setPage = serverPagination
    ? (p) => onPageChange?.(typeof p === 'function' ? p(page) : p)
    : setInternalPage

  // Reset to first page when filtering / sorting changes upstream (client mode only).
  React.useEffect(() => { if (!serverPagination) setInternalPage(0) }, [rows, sort, serverPagination])

  const sortedRows = useMemo(() => {
    if (serverPagination || !sort?.field) return rows
    const col = cols.find(c => c.field === sort.field)
    const cmp = col?.numeric || col?.sales || col?.percent
      ? (a, b) => (Number(a[sort.field]) || 0) - (Number(b[sort.field]) || 0)
      : (a, b) => String(a[sort.field] ?? '').localeCompare(String(b[sort.field] ?? ''), 'pl')
    const sorted = [...rows].sort(cmp)
    return sort.dir === 'desc' ? sorted.reverse() : sorted
  }, [rows, sort, cols, serverPagination])

  const totalRows = serverPagination ? (rowCount ?? rows.length) : sortedRows.length
  const totalPages = pageSize > 0 ? Math.max(1, Math.ceil(totalRows / pageSize)) : 1
  const visibleRows = serverPagination
    ? sortedRows                             // server already paginated
    : pageSize > 0
      ? sortedRows.slice(page * pageSize, (page + 1) * pageSize)
      : sortedRows

  const totalsRow = useMemo(() => {
    if (!totals) return null
    const out = {}
    let any = false
    for (const c of cols) {
      if (c.sales || c.numeric) {
        out[c.field] = sortedRows.reduce((a, r) => a + (Number(r[c.field]) || 0), 0)
        any = true
      }
    }
    return any ? out : null
  }, [totals, cols, sortedRows])

  // Trailing spacer track: Section renders tables edge-to-edge (no padding)
  // inside a rounded, overflow:hidden card. Without a real buffer track,
  // the last column's content sits flush against the card's own right
  // edge — and since a horizontally-scrollable container's own end-padding
  // isn't reliably reachable by scrolling in every browser, adding it as
  // CSS padding here doesn't fix it. A real (empty) grid track at the end
  // always is reachable, so the last column never visually collides with
  // the card's border-radius.
  const TRAILING_SPACER_PX = 16
  const gridTemplate = cols.map(c => c.gridSize).join(' ') + ` ${TRAILING_SPACER_PX}px`
  const minWidthPx = cols.reduce((s, c) => s + (c.minWidthPx || 0), 0) + TRAILING_SPACER_PX

  const rowHeight = density === 'compact' ? 32 : 40
  const headerHeight = density === 'compact' ? 32 : 36

  const headerStyle = {
    display: 'grid',
    gridTemplateColumns: gridTemplate,
    gap: 12,
    padding: '0 16px',
    minWidth: minWidthPx ? `${minWidthPx}px` : 'auto',
    height: headerHeight,
    background: theme.bgSub,
    borderBottom: `1px solid ${theme.border}`,
    fontSize: 10, fontWeight: 700, color: theme.textDim,
    letterSpacing: 0.6, textTransform: 'uppercase',
    fontFamily: T.fontUI,
    position: 'sticky', top: 0, zIndex: 1,
  }

  const onHeaderClick = (col) => {
    if (col.sortable === false) return
    if (sort?.field === col.field) {
      setSort({ field: col.field, dir: sort.dir === 'asc' ? 'desc' : 'asc' })
    } else {
      setSort({ field: col.field, dir: col.numeric || col.sales || col.percent ? 'desc' : 'asc' })
    }
  }

  // Empty / loading / error state.
  if (loading) {
    return (
      <Wrap theme={theme}>
        <div style={{ padding: 32, textAlign: 'center', color: theme.textDim, fontSize: 13 }}>
          Ładowanie…
        </div>
      </Wrap>
    )
  }
  if (error) {
    return (
      <Wrap theme={theme}>
        <div style={{
          padding: '12px 16px',
          background: theme.badSub, color: theme.bad,
          fontSize: 12.5, fontWeight: 500,
        }}>{error}</div>
      </Wrap>
    )
  }

  return (
    <Wrap theme={theme}>
      <div style={{ overflowX: 'auto' }}>
        {/* Header */}
        <div style={headerStyle}>
          {cols.map(c => {
            const isSorted = sort?.field === c.field
            const dir = isSorted ? sort.dir : null
            const headerJustify = c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start'
            return (
              <span key={c.field}
                onClick={() => onHeaderClick(c)}
                style={{
                  display: 'flex', alignItems: 'center',
                  justifyContent: headerJustify, gap: 4,
                  cursor: c.sortable === false ? 'default' : 'pointer',
                  userSelect: 'none',
                  color: isSorted ? theme.text : theme.textDim,
                }}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.header}
                </span>
                {c.sortable !== false && (
                  <span style={{
                    fontFamily: T.fontMono,
                    fontSize: 10, lineHeight: 1,
                    color: isSorted ? theme.text : 'transparent',
                    transition: 'color 100ms',
                  }}>
                    {dir === 'desc' ? '↓' : dir === 'asc' ? '↑' : '↕'}
                  </span>
                )}
              </span>
            )
          })}
        </div>

        {/* Rows */}
        {visibleRows.length === 0 && (
          <div style={{
            padding: '24px 16px', textAlign: 'center',
            color: theme.textDim, fontSize: 12.5,
          }}>{emptyText}</div>
        )}
        {visibleRows.map((row, i) => {
          const id = getRowId ? getRowId(row, i) : (row.id ?? row[cols[0]?.field] ?? i)
          const hasSale = cols.some(c => c.sales && Number(row[c.field]) > 0)
          return (
            <div key={id}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                display: 'grid',
                gridTemplateColumns: gridTemplate,
                gap: 12,
                padding: '0 16px',
                minHeight: rowHeight,
                minWidth: minWidthPx ? `${minWidthPx}px` : 'auto',
                alignItems: 'center',
                fontSize: density === 'compact' ? 12 : 12.5,
                color: theme.text,
                background: hasSale ? `${theme.good}10` : 'transparent',
                borderBottom: `1px solid ${theme.border}`,
                cursor: onRowClick ? 'pointer' : 'default',
                transition: 'background 80ms',
              }}
              onMouseEnter={(e) => e.currentTarget.style.background = theme.bgSub}
              onMouseLeave={(e) => e.currentTarget.style.background = hasSale ? `${theme.good}10` : 'transparent'}
            >
              {cols.map(c => <Cell key={c.field} col={c} row={row} theme={theme} />)}
            </div>
          )
        })}

        {/* Totals row */}
        {totalsRow && visibleRows.length > 0 && (
          <div style={{
            display: 'grid', gridTemplateColumns: gridTemplate, gap: 12,
            padding: '0 16px', minHeight: rowHeight,
            minWidth: minWidthPx ? `${minWidthPx}px` : 'auto',
            alignItems: 'center',
            fontSize: 12.5,
            background: theme.bgSub,
            borderTop: `1px solid ${theme.border}`,
            fontFamily: T.fontMono, fontWeight: 700,
          }}>
            {cols.map((c, idx) => {
              if (c.sales || c.numeric) {
                return <Cell key={c.field} col={c} row={{ [c.field]: totalsRow[c.field] }} theme={theme} bold />
              }
              if (idx === 0) {
                return (
                  <span key={c.field} style={{
                    color: theme.textDim, fontFamily: T.fontUI, fontSize: 11,
                    fontWeight: 700, letterSpacing: 0.5, textTransform: 'uppercase',
                  }}>Razem</span>
                )
              }
              return <span key={c.field} />
            })}
          </div>
        )}
      </div>

      {/* Pagination footer */}
      {pageSize > 0 && totalRows > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '8px 16px',
          borderTop: `1px solid ${theme.border}`,
          fontSize: 11.5, color: theme.textSub,
          fontFamily: T.fontUI,
        }}>
          <span>Wierszy: {totalRows}</span>
          <div style={{ flex: 1 }} />
          <span style={{ color: theme.textDim }}>na stronę:</span>
          <select value={pageSize}
            onChange={(e) => {
              const n = Number(e.target.value)
              setPageSize(n); setPage(0)
              if (serverPagination) onPageSizeChange?.(n)
            }}
            style={{
              border: `1px solid ${theme.border}`, borderRadius: 6,
              padding: '3px 6px', background: theme.surface,
              fontFamily: T.fontUI, fontSize: 11.5, color: theme.text, cursor: 'pointer',
            }}>
            {pageSizeOptions.map(n => <option key={n} value={n}>{n}</option>)}
          </select>
          <span style={{ color: theme.textDim }}>
            {page * pageSize + 1}–{Math.min((page + 1) * pageSize, totalRows)} z {totalRows}
          </span>
          <button
            onClick={() => setPage(p => Math.max(0, p - 1))}
            disabled={page === 0}
            style={pagerBtn(theme, page === 0)}
          >‹</button>
          <button
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            disabled={page >= totalPages - 1}
            style={pagerBtn(theme, page >= totalPages - 1)}
          >›</button>
        </div>
      )}
    </Wrap>
  )
}

// ─ helpers ──────────────────────────────────────────────────────
function Wrap({ children, theme }) {
  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.border}`,
      borderRadius: 10, overflow: 'hidden',
    }}>{children}</div>
  )
}

function pagerBtn(theme, disabled) {
  return {
    width: 24, height: 24, borderRadius: 5,
    border: `1px solid ${theme.border}`,
    background: theme.surface, color: theme.text,
    fontFamily: T.fontMono, fontSize: 14, fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.4 : 1,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  }
}

function Cell({ col, row, theme, bold = false }) {
  const value = row[col.field]
  const align = col.align
  const isMono = col.numeric || col.sales || col.phone || col.percent
  const baseStyle = {
    textAlign: align,
    display: 'flex', alignItems: 'center',
    justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
    fontFamily: isMono ? T.fontMono : T.fontUI,
    color: theme.text,
    overflow: 'hidden',
  }
  const inner = (() => {
    if (col.render) return col.render(value, row)
    if (col.percent) {
      const v = Number(value) || 0
      const [warnAt = 60, goodAt = 100] = col.percent.thresholds || []
      const kind = v >= goodAt ? 'good' : v >= warnAt ? 'warn' : 'bad'
      const fmtTxt = col.format ? col.format(v) : `${v.toFixed(1).replace('.', ',')}%`
      return <Badge kind={kind} theme={theme}>{fmtTxt}</Badge>
    }
    if (col.sales) {
      const isZero = !value || Number(value) === 0
      return (
        <span style={{
          color: isZero ? theme.textDim : theme.good,
          fontWeight: bold ? 700 : (isZero ? 400 : 600),
        }}>{value == null ? '—' : (col.format ? col.format(value) : fmt.int(value))}</span>
      )
    }
    if (col.phone) {
      if (!value) return <span style={{ color: theme.textDim }}>—</span>
      return (
        <a href={`tel:${value}`} style={{
          color: theme.accent, textDecoration: 'underline',
        }}>{value}</a>
      )
    }
    if (col.email) {
      if (!value) return <span style={{ color: theme.textDim }}>—</span>
      return (
        <a href={`mailto:${value}`} style={{
          color: theme.accent, textDecoration: 'underline',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{value}</a>
      )
    }
    if (col.numeric) {
      return (
        <span style={{
          color: value == null ? theme.textDim : theme.text,
          fontWeight: bold ? 700 : 500,
        }}>{value == null ? '—' : (col.format ? col.format(value) : fmt.int(value))}</span>
      )
    }
    return (
      <span style={{
        color: value == null || value === '' ? theme.textDim : theme.text,
        fontWeight: 500,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>{value == null || value === '' ? '—' : (col.format ? col.format(value) : String(value))}</span>
    )
  })()
  return <span style={baseStyle}>{inner}</span>
}

function Badge({ kind = 'neutral', children, theme }) {
  const c = {
    good:    { bg: theme.goodSub, fg: theme.good, br: `${theme.good}33` },
    warn:    { bg: theme.warnSub, fg: theme.warn, br: `${theme.warn}33` },
    bad:     { bg: theme.badSub,  fg: theme.bad,  br: `${theme.bad}33`  },
    neutral: { bg: theme.bgSub, fg: theme.textSub, br: theme.border },
  }[kind] || { bg: theme.bgSub, fg: theme.textSub, br: theme.border }
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 8px', borderRadius: 999,
      background: c.bg, color: c.fg,
      border: `1px solid ${c.br}`,
      fontSize: 11, fontWeight: 600, lineHeight: 1.4,
      whiteSpace: 'nowrap',
    }}>{children}</span>
  )
}

function normalizeColumn(c) {
  // Accept MUI-DataGrid–shaped column defs as a compat layer:
  //   headerName → header
  //   renderCell({ value, row, ... }) → render(value, row)
  //   valueGetter → format/derive accessor
  //   type: 'number' → numeric: true
  const out = { ...c }
  if (out.header == null && out.headerName) out.header = out.headerName
  if (!out.numeric && out.type === 'number') out.numeric = true
  if (!out.render && typeof out.renderCell === 'function') {
    const fn = out.renderCell
    out.render = (value, row) => fn({ value, row, id: row?.id, field: out.field })
  }
  if (typeof out.valueGetter === 'function') {
    const vg = out.valueGetter
    const baseRender = out.render
    if (!baseRender) {
      out.render = (_v, row) => vg(row?.[out.field], row)
    }
  }
  // flex (MUI) → fr
  if (out.width == null && typeof out.flex === 'number' && out.flex > 0) {
    out.width = `${out.flex}fr`
  }
  const numeric = !!(out.numeric || out.sales || out.percent)
  const align = out.align ?? (numeric ? 'right' : 'left')
  let gridSize, minWidthPx
  if (typeof out.width === 'number') { gridSize = `${out.width}px`; minWidthPx = out.width }
  else if (typeof out.width === 'string') {
    gridSize = out.width
    if (out.width.endsWith('px')) {
      minWidthPx = parseInt(out.width)
    } else {
      // A fr-based width (e.g. '1.6fr') has no floor of its own — once the
      // fixed-px columns + gaps approach the container width, it gets
      // squeezed toward 0 (content still in the DOM, just rendered with no
      // width). `minWidth` needs to (a) count toward the row container's
      // total minWidth so the container reserves enough space, AND
      // (b) wrap the track itself in minmax() so THIS column doesn't give
      // up its own floor to its neighbors even when space is tight.
      minWidthPx = out.minWidth || 0
      if (out.minWidth) gridSize = `minmax(${out.minWidth}px, ${out.width})`
    }
  } else {
    gridSize = '1fr'; minWidthPx = out.minWidth || 100
  }
  return { ...out, align, gridSize, minWidthPx }
}
